/**
 * Admin Management Page
 * Manage admin and moderator roles.
 *
 * Round 39: per-admin access. A super admin sees and can do everything; every
 * other admin is given a list of tabs (and, for AI Workforce, a list of
 * agents). Everything else is greyed out in their sidebar and refused by the
 * server. Set it when adding an admin, change it any time with Edit.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  UserCog,
  Plus,
  Trash2,
  Search,
  Crown,
  Users,
  Pencil,
  Lock,
} from 'lucide-react';

const roleColors = {
  super_admin: 'bg-amber-100 text-amber-700',
  admin: 'bg-emerald-100 text-emerald-700',
  moderator: 'bg-blue-100 text-blue-700'
};

const emptyAccess = () => ({ all: false, tabs: [], agents: null, financial_access: false });

/** The access editor: role, full access or a checklist of tabs, and which agents. */
const AccessEditor = ({ catalog, role, setRole, access, setAccess, lockRole }) => {
  const tabsByGroup = (catalog?.tabs || []).reduce((acc, t) => { (acc[t.group] = acc[t.group] || []).push(t); return acc; }, {});
  const isSuper = role === 'super_admin';
  const toggleTab = (key) => setAccess((a) => ({ ...a, tabs: a.tabs.includes(key) ? a.tabs.filter((k) => k !== key) : [...a.tabs, key] }));
  const toggleAgent = (name) => setAccess((a) => {
    const cur = Array.isArray(a.agents) ? a.agents : (catalog?.agents || []).map((x) => x.name);
    return { ...a, agents: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name] };
  });
  const agentsOn = Array.isArray(access.agents) ? access.agents : (catalog?.agents || []).map((x) => x.name);
  const workforceOn = access.all || access.tabs.includes('workforce') || access.tabs.includes('current-events');

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-700 block mb-2">Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value)} disabled={lockRole} data-testid="admin-role-select"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50">
          <option value="moderator">Moderator</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {isSuper ? 'Super admins see and can do everything, including this page.' : 'Admins and moderators only see what you tick below.'}
        </p>
      </div>

      {!isSuper && (
        <>
          <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
            <input type="checkbox" checked={!!access.all} onChange={(e) => setAccess((a) => ({ ...a, all: e.target.checked }))} className="w-4 h-4 rounded border-gray-300" data-testid="admin-access-all" />
            Full access (every tab except Email Broadcast and Admin Management)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
            <input type="checkbox" checked={!!access.financial_access} onChange={(e) => setAccess((a) => ({ ...a, financial_access: e.target.checked }))} className="w-4 h-4 rounded border-gray-300" data-testid="admin-access-financial" />
            Financials ($) — money reports
          </label>

          {!access.all && (
            <div className="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto space-y-3" data-testid="admin-access-tabs">
              {Object.entries(tabsByGroup).map(([group, tabs]) => (
                <div key={group}>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">{group}</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {tabs.map((t) => {
                      const locked = t.super_only || t.financial;
                      return (
                        <label key={t.key} className={`flex items-center gap-2 text-sm ${locked ? 'text-gray-300' : 'text-gray-800 cursor-pointer'}`} title={t.super_only ? 'Super admins only' : t.financial ? 'Use the Financials toggle above' : ''}>
                          <input type="checkbox" disabled={locked || t.always}
                            checked={t.always || (!locked && access.tabs.includes(t.key))}
                            onChange={() => toggleTab(t.key)} className="w-4 h-4 rounded border-gray-300" data-testid={`admin-access-tab-${t.key}`} />
                          {t.label}{locked && <Lock className="w-3 h-3" />}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {workforceOn && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">AI Workforce agents this admin may open</p>
                <button type="button" className="text-xs text-emerald-700 hover:underline" onClick={() => setAccess((a) => ({ ...a, agents: Array.isArray(a.agents) ? null : [] }))}>
                  {Array.isArray(access.agents) ? 'All agents' : 'Pick agents'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 border border-gray-200 rounded-lg p-3" data-testid="admin-access-agents">
                {(catalog?.agents || []).map((ag) => (
                  <label key={ag.name} className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer" title={ag.purpose}>
                    <input type="checkbox" checked={agentsOn.includes(ag.name)} onChange={() => toggleAgent(ag.name)} className="w-4 h-4 rounded border-gray-300" />
                    {ag.name.charAt(0) + ag.name.slice(1).toLowerCase()}
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const accessSummary = (admin) => {
  if (admin.role === 'super_admin') return 'Everything';
  const a = admin.access || {};
  if (a.all) return 'All tabs';
  const n = (a.tab_keys || a.tabs || []).length;
  const agents = Array.isArray(a.agents) ? ` · ${a.agents.length} agent${a.agents.length === 1 ? '' : 's'}` : '';
  return `${n} tab${n === 1 ? '' : 's'}${agents}`;
};

const AdminManagementPage = () => {
  const { api, admin: currentAdmin } = useAdmin();
  const [admins, setAdmins] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState(null);          // an admin row being edited
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('moderator');
  const [access, setAccess] = useState(emptyAccess());
  const [saving, setSaving] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const [a, c] = await Promise.allSettled([api.get('/api/admin/admins'), api.get('/api/admin/admins/access-catalog')]);
      setAdmins(a.status === 'fulfilled' ? a.value.data.admins : []);
      if (c.status === 'fulfilled') setCatalog(c.value.data);
    } catch (error) {
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await api.get(`/api/admin/users?query=${encodeURIComponent(searchQuery)}&page_size=10`);
      setSearchResults(response.data.users.filter(u => !u.is_admin));
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const closeModals = () => {
    setShowAddModal(false); setEditing(null); setSelectedUser(null); setSearchQuery(''); setSearchResults([]);
    setSelectedRole('moderator'); setAccess(emptyAccess());
  };

  const accessBody = () => ({ all: !!access.all, tabs: access.tabs, agents: access.agents, financial_access: !!access.financial_access });

  const assignRole = async () => {
    if (!selectedUser) return toast.error('Please select a user');
    setSaving(true);
    try {
      await api.post('/api/admin/admins/assign', { user_id: selectedUser.id, role: selectedRole, access: accessBody() });
      if (selectedRole !== 'super_admin') {
        await api.put(`/api/admin/admins/${selectedUser.id}/access`, { financial_access: !!access.financial_access });
      }
      toast.success('Admin added');
      closeModals();
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign role');
    } finally { setSaving(false); }
  };

  const openEdit = (row) => {
    const a = row.access || {};
    setEditing(row);
    setSelectedRole(row.role || 'admin');
    setAccess({ all: !!a.all, tabs: a.tabs || [], agents: Array.isArray(a.agents) ? a.agents : null, financial_access: !!row.financial_access });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/admins/${editing.user.id}/access`, { role: selectedRole, ...accessBody() });
      toast.success('Access updated');
      closeModals();
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally { setSaving(false); }
  };

  const removeAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this admin?')) return;
    try {
      await api.delete(`/api/admin/admins/${userId}`);
      toast.success('Admin role removed');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const Modal = ({ title, subtitle, children, onSave, saveLabel, canSave }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[92vh] flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">{children}</div>
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={closeModals} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium">Cancel</button>
          <button onClick={onSave} disabled={!canSave || saving} data-testid="admin-modal-save"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
          <p className="text-gray-500 text-sm mt-1">Who is on the team, and which parts of the panel each person can open</p>
        </div>
        <button onClick={() => { setAccess(emptyAccess()); setSelectedRole('moderator'); setShowAddModal(true); }} data-testid="add-admin-btn"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium">
          <Plus className="w-4 h-4" />
          Add Admin
        </button>
      </div>

      {/* Current User Info */}
      <div className="bg-gradient-to-r from-emerald-50 to-rose-50 rounded-xl p-6 border border-emerald-100">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white text-xl font-bold">
            {currentAdmin?.name?.charAt(0) || 'A'}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{currentAdmin?.name}</p>
            <p className="text-sm text-gray-500">{currentAdmin?.email}</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${roleColors[currentAdmin?.role] || roleColors.admin}`}>
              <Crown className="w-3 h-3 mr-1" />
              {currentAdmin?.role?.replace('_', ' ') || 'Admin'}
            </span>
          </div>
        </div>
      </div>

      {/* Admins List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-400" />
            Team Members
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : admins.length === 0 ? (
          <div className="text-center py-12">
            <UserCog className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No admin users found</p>
            <p className="text-sm text-gray-400 mt-1">Only super admins can view this list</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {admins.map((admin, i) => (
              <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50 gap-3" data-testid={`admin-row-${admin.user?.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white font-medium shrink-0">
                    {admin.user?.name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{admin.user?.name}</p>
                    <p className="text-sm text-gray-500 truncate">{admin.user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[admin.role] || roleColors.moderator}`}>
                    {admin.role?.replace('_', ' ')}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium" title="What this admin can open">
                    {accessSummary(admin)}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${admin.financial_access ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    $ {admin.financial_access ? (admin.role === 'super_admin' ? 'Full Access' : 'Access') : 'No Access'}
                  </span>
                  {admin.user?.id !== currentAdmin?.id && (
                    <>
                      <button onClick={() => openEdit(admin)} className="p-2 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors" title="Edit access" data-testid={`admin-edit-${admin.user?.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeAdmin(admin.user?.id)} className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Remove Admin">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <Modal title="Add Admin" subtitle="Find the member, choose a role, then tick what they can open" onSave={assignRole} saveLabel="Add admin" canSave={!!selectedUser}>
          <div>
            <label className="text-sm text-gray-700 block mb-2">Search User</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                  placeholder="Search by name or email..." className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <button onClick={searchUsers} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">Search</button>
            </div>
          </div>
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
              {searchResults.map((user) => (
                <button key={user.id} onClick={() => setSelectedUser(user)}
                  className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors ${selectedUser?.id === user.id ? 'bg-emerald-50 border-l-2 border-emerald-500' : ''}`}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white text-sm">{user.name?.charAt(0)}</div>
                  <div><p className="font-medium text-gray-900 text-sm">{user.name}</p><p className="text-xs text-gray-500">{user.email}</p></div>
                </button>
              ))}
            </div>
          )}
          {selectedUser && (
            <div className="bg-emerald-50 rounded-lg p-3"><p className="text-sm text-emerald-700">Selected: <strong>{selectedUser.name}</strong></p></div>
          )}
          <AccessEditor catalog={catalog} role={selectedRole} setRole={setSelectedRole} access={access} setAccess={setAccess} />
        </Modal>
      )}

      {/* Edit Access Modal */}
      {editing && (
        <Modal title={`Edit access — ${editing.user?.name}`} subtitle="Changes apply the next time this admin loads a page" onSave={saveEdit} saveLabel="Save access" canSave>
          <AccessEditor catalog={catalog} role={selectedRole} setRole={setSelectedRole} access={access} setAccess={setAccess} lockRole={false} />
        </Modal>
      )}
    </div>
  );
};

export default AdminManagementPage;
